import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { F0TagStatus } from '@factorialco/f0-react'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import { Delete, Download } from '@factorialco/f0-react/icons/app'
import Envelope from '@factorialco/f0-react/icons/app/Envelope'
import {
  AtsDeleteUnsendOfferLetterDocument,
  AtsJobApplicationFilesOfferLetterDocument,
  AtsUnsendOfferLetterItemFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  unsendOfferLetter: AtsUnsendOfferLetterItemFragment
  applicationId: string
  companyId: string
  jobPostingId: string
  onSend: () => void
}

export const UnsendOfferLetterItem = ({
  unsendOfferLetter,
  applicationId,
  companyId,
  jobPostingId,
  onSend,
}: Props) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { id, createdAt, file, authorFullName } = unsendOfferLetter

  const [deleteUnsendOfferLetter, { loading: deleting }] = useMutation(
    AtsDeleteUnsendOfferLetterDocument,
    {
      extractorFn: (data) => data.atsMutations.deleteAtsFile,
      refetchQueries: [
        {
          query: AtsJobApplicationFilesOfferLetterDocument,
          variables: { applicationId, companyId, jobPostingId },
        },
      ],
    }
  )

  const onDelete = async () => {
    const result = await deleteUnsendOfferLetter({
      variables: { id },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.candidates.files.unsend_offer_letter.delete_success'),
      })
      setShowDeleteModal(false)
    }

    return handled
  }

  const label = i18n.t('ats.candidates.files.unsend_offer_letter.info', {
    created_at: moment(createdAt).format('ll'),
    author: authorFullName ?? '',
  })

  return (
    <>
      <Box
        flexDirection="row"
        gap="s2"
        paddingX="s16"
        paddingY="s12"
        justifyContent="spaceBetween"
        width="full"
        alignItems="center"
        borderRadius={{ all: 'abs016' }}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Box alignItems="center" flexGrow flexDirection="row" gap="s16">
          <DocumentType mimetype={file.contentType} />
          <Box overflowX="hidden" maxWidth="s300" gap="s4">
            <Text.Normal size="200" weight="medium" color="textPrimary">
              {file.filename}
            </Text.Normal>
            <Text.Normal size="200">{label}</Text.Normal>
          </Box>
        </Box>

        <Box flexDirection="row" gap="s12" justifyContent="flexEnd" alignItems="center">
          <F0TagStatus
            text={i18n.t('ats.candidates.files.unsend_offer_letter.status')}
            variant="warning"
          />
          <Dropdown
            items={[
              {
                label: i18n.t('ats.candidates.files.unsend_offer_letter.send'),
                icon: Envelope,
                onClick: onSend,
              },
              {
                label: i18n.t('documents.download'),
                href: file.url,
                icon: Download,
              },
              {
                label: i18n.t('documents.delete'),
                icon: Delete,
                onClick: () => setShowDeleteModal(true),
              },
            ]}
          />
        </Box>
      </Box>

      {showDeleteModal && (
        <Modal
          title={i18n.t('ats.candidates.files.unsend_offer_letter.delete_modal.title')}
          description={i18n.t('ats.candidates.files.unsend_offer_letter.delete_modal.description')}
          illustration="delete"
          onClose={() => setShowDeleteModal(false)}
          onSubmit={onDelete}
          footer={
            <Button type="negative" disabled={deleting} submit>
              {i18n.t('ats.candidates.files.unsend_offer_letter.delete_modal.submit')}
            </Button>
          }
        />
      )}
    </>
  )
}
